import { useEffect, useRef } from "react";
import { TableLoadingState } from "../AsyncState";
import { observeInfiniteScroll } from "../../helper/observeInfiniteScroll";

/**
 * @template T
 * @typedef {Object} PaginatedResponse
 * @property {number} count
 * @property {number} page
 * @property {number} page_size
 * @property {number} total_pages
 * @property {string|null} next
 * @property {string|null} previous
 * @property {T[]} results
 */

export const DEFAULT_PAGE_SIZE = 50;

export function buildPaginatedUrl(url, page, pageSize = DEFAULT_PAGE_SIZE) {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set("page", String(Math.max(1, Number(page) || 1)));
  if (pageSize !== null) {
    parsed.searchParams.set(
      "page_size",
      String(Math.min(100, Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE))),
    );
  }
  return parsed.origin === window.location.origin
    ? `${parsed.pathname}${parsed.search}`
    : parsed.toString();
}

export function paginationFromResponse(response, requestedPage = 1) {
  const results = Array.isArray(response?.results) ? response.results : [];
  return {
    results,
    count: Number(response?.count) || 0,
    page: Math.max(1, Number(response?.page) || requestedPage),
    pageSize: Number(response?.page_size) || results.length,
    totalPages: Math.max(0, Number(response?.total_pages) || 0),
  };
}

export async function collectPaginatedResults(firstResponse, fetchPage) {
  const items = Array.isArray(firstResponse?.results)
    ? [...firstResponse.results]
    : [];
  const totalPages = Math.max(1, Number(firstResponse?.total_pages) || 1);

  for (let page = 2; page <= totalPages; page += 1) {
    const response = await fetchPage(page);
    if (!Array.isArray(response?.results)) {
      throw new Error(`Invalid paginated response for page ${page}`);
    }
    items.push(...response.results);
  }

  return items;
}

export default function Pagination({
  page,
  totalPages,
  onPageChange,
  loading = false,
  scrollRootRef,
  axis = "vertical",
}) {
  const safeTotalPages = Math.max(0, Number(totalPages) || 0);
  const safePage = safeTotalPages === 0 ? 1 : Math.min(page, safeTotalPages);
  const triggerRef = useRef(null);
  const hasMore = safeTotalPages > 0 && safePage < safeTotalPages;

  useEffect(() => {
    const target = triggerRef.current;
    if (!target || !hasMore || loading) return undefined;

    const previousElement = target.previousElementSibling;
    const tableScrollContainer = scrollRootRef?.current ||
      (previousElement?.matches?.(".overflow-auto") ? previousElement : null) ||
      previousElement?.querySelector?.(".overflow-auto") ||
      target.closest?.(".overflow-auto");
    return observeInfiniteScroll(target, () => onPageChange(safePage + 1), {
      root: tableScrollContainer,
      axis,
    });
  }, [axis, hasMore, loading, onPageChange, safePage, scrollRootRef]);

  return (
    <div ref={triggerRef} className="flex min-h-1 w-full items-center justify-center">
      {loading && safeTotalPages > 0 && (
        <TableLoadingState more className="!py-3" />
      )}
    </div>
  );
}
